import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ThemeProvider } from "next-themes";
import { ThemeMetaSync } from "./components/ThemeMetaSync";
import { LanguageInitializer } from "./components/LanguageInitializer";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Courses from "./pages/Courses";
import { AdminRoute } from "./components/AdminRoute";
import { InstructorRoute } from "./components/InstructorRoute";
import { AuthRoute } from "./components/AuthRoute";
import AppShellLayout from "./layouts/AppShellLayout";
import { useScrollbarVisibility } from "./hooks/useScrollbarVisibility";
import { ScrollToTop } from "./components/ScrollToTop";
import { SiteWidePromotionBanner } from "./components/SiteWidePromotionBanner";
import { PromotionPopup } from "./components/PromotionPopup";
import { MaintenanceGuard } from "./components/MaintenanceGuard";
import { NativeShellGate } from "./components/NativeShellGate";
import { PushNotificationsBootstrap } from "./components/PushNotificationsBootstrap";
import RouteFallback from "./components/RouteFallback";
import { ConsentProvider } from "./lib/consent";
import { CookieBanner } from "./components/consent/CookieBanner";
import { PrivacyTrigger } from "./components/consent/PrivacyTrigger";
const AccountPrivacy = lazy(() => import("./pages/account/Privacy"));
const AccountDangerZone = lazy(() => import("./pages/account/DangerZone"));

// Tiny inline component to redirect old plural certificate URLs to the singular form
function CertificateRedirect() {
  const { certificateId } = useParams<{ certificateId: string }>();
  return <Navigate to={`/certificate/${certificateId ?? ""}`} replace />;
}

// Lazy load non-critical routes
const Dashboard = lazy(() => import("./pages/Dashboard"));
const MyCourses = lazy(() => import("./pages/MyCourses"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const CourseViewer = lazy(() => import("./pages/CourseViewer"));
const Certificate = lazy(() => import("./pages/Certificate"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PaymentHistory = lazy(() => import("./pages/PaymentHistory"));
const UserSubscriptionManagement = lazy(() => import("./pages/SubscriptionManagement"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Support = lazy(() => import("./pages/Support"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const NewsletterUnsubscribe = lazy(() => import("./pages/NewsletterUnsubscribe"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Gamification = lazy(() => import("./pages/Gamification"));
const LearningAnalytics = lazy(() => import("./pages/LearningAnalytics"));
const MyAnalytics = lazy(() => import("./pages/MyAnalytics"));
const MyPerformance = lazy(() => import("./pages/MyPerformance"));
const Profile = lazy(() => import("./pages/Profile"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const Settings = lazy(() => import("./pages/Settings"));
const Messages = lazy(() => import("./pages/Messages"));
const AiSettings = lazy(() => import("./pages/AiSettings"));
const Announcements = lazy(() => import("./pages/Announcements"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CheckoutPix = lazy(() => import("./pages/CheckoutPix"));
const CheckoutPixCart = lazy(() => import("./pages/CheckoutPixCart"));

// Prep Premium
const PrepLanding = lazy(() => import("./pages/prep/Landing"));
const PrepDashboard = lazy(() => import("./pages/prep/PrepDashboard"));
const PrepContests = lazy(() => import("./pages/prep/PrepContests"));
const PrepContestDetail = lazy(() => import("./pages/prep/PrepContestDetail"));
const PrepSimulationRoom = lazy(() => import("./pages/prep/PrepSimulationRoom"));
const PrepSimulationResult = lazy(() => import("./pages/prep/PrepSimulationResult"));
const PrepAdminHub = lazy(() => import("./pages/admin/prep/PrepAdminHub"));
const PrepSettingsPage = lazy(() => import("./pages/admin/prep/PrepSettings"));
const PrepProductsPage = lazy(() => import("./pages/admin/prep/PrepProducts"));
const PrepQuestionsPage = lazy(() => import("./pages/admin/prep/PrepQuestions"));
const PrepRubricsPage = lazy(() => import("./pages/admin/prep/PrepRubrics"));
const PrepAdminContestsPage = lazy(() => import("./pages/admin/prep/PrepAdminContests"));
const PrepInterviews = lazy(() => import("./pages/prep/PrepInterviews"));
const PrepInterviewTrackDetail = lazy(() => import("./pages/prep/PrepInterviewTrackDetail"));
const PrepMockNew = lazy(() => import("./pages/prep/PrepMockNew"));
const PrepMockRoom = lazy(() => import("./pages/prep/PrepMockRoom"));
const PrepMockFeedback = lazy(() => import("./pages/prep/PrepMockFeedback"));
const PrepAdminInterviewTracks = lazy(() => import("./pages/admin/prep/PrepAdminInterviewTracks"));
const PrepAccessPage = lazy(() => import("./pages/admin/prep/PrepAccess"));
const PrepStudyPlan = lazy(() => import("./pages/prep/PrepStudyPlan"));

// Lazy load admin routes
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ControlCenter = lazy(() => import("./pages/admin/ControlCenter"));
const QuizAnalyticsDashboard = lazy(() => import("./pages/admin/QuizAnalyticsDashboard"));
const AiQuizTools = lazy(() => import("./pages/admin/AiQuizTools"));
const UsersManagement = lazy(() => import("./pages/admin/UsersManagement"));
const CoursesManagement = lazy(() => import("./pages/admin/CoursesManagement"));
const CourseEditor = lazy(() => import("./pages/admin/CourseEditor"));
const ActivityTracking = lazy(() => import("./pages/admin/ActivityTracking"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const CommunicationsManagement = lazy(() => import("./pages/admin/CommunicationsManagement"));
const EmailMonitor = lazy(() => import("./pages/admin/EmailMonitor"));
const NewsletterManagement = lazy(() => import("./pages/admin/NewsletterManagement"));
const NewsletterAnalytics = lazy(() => import("./pages/admin/NewsletterAnalytics"));
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"));
const InstructorsManagement = lazy(() => import("./pages/admin/InstructorsManagement"));
const AdvancedAnalytics = lazy(() => import("./pages/admin/AdvancedAnalytics"));
const FeedbackManagement = lazy(() => import("./pages/admin/FeedbackManagement"));
const PromotionsManagement = lazy(() => import("./pages/admin/PromotionsManagement"));
const DiscountsManagement = lazy(() => import("./pages/admin/DiscountsManagement"));
const SecurityDashboard = lazy(() => import("./pages/admin/SecurityDashboard"));
const SecurityAudit = lazy(() => import("./pages/admin/SecurityAudit"));
const IntegrationTesting = lazy(() => import("./pages/admin/IntegrationTesting"));
const SecurityVulnerabilityTesting = lazy(() => import("./pages/admin/SecurityVulnerabilityTesting"));
const SecurityScanner = lazy(() => import("./pages/admin/SecurityScanner"));
const SecurityCompliance = lazy(() => import("./pages/admin/SecurityCompliance"));
const UserIntelligence = lazy(() => import("./pages/admin/UserIntelligence"));
const ABTestingDashboard = lazy(() => import("./pages/admin/ABTestingDashboard"));
const StudentEnrollments = lazy(() => import("./pages/admin/StudentEnrollments"));
const SubscriptionManagement = lazy(() => import("./pages/admin/SubscriptionManagement"));
const PlatformPlans = lazy(() => import("./pages/admin/PlatformPlans"));
const PaymentOperations = lazy(() => import("./pages/admin/PaymentOperations"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminAbandonedCarts = lazy(() => import("./pages/admin/AdminAbandonedCarts"));
const AdminRecommendations = lazy(() => import("./pages/admin/AdminRecommendations"));
const BundlesManagement = lazy(() => import("./pages/admin/BundlesManagement"));
const Bundles = lazy(() => import("./pages/Bundles"));
const BundleDetail = lazy(() => import("./pages/BundleDetail"));
const PaymentHealthDashboard = lazy(() => import("./pages/admin/PaymentHealthDashboard"));
const AccessManagement = lazy(() => import("./pages/admin/AccessManagement"));
const CustomerSupport = lazy(() => import("./pages/admin/CustomerSupport"));
const RoleManagement = lazy(() => import("./pages/admin/RoleManagement"));
const AdminCertificatesManagement = lazy(() => import("./pages/admin/CertificatesManagement"));
const SilvaCoinsManagement = lazy(() => import("./pages/admin/SilvaCoinsManagement"));
const BadgesManagement = lazy(() => import("./pages/admin/BadgesManagement"));
const AchievementsManagement = lazy(() => import("./pages/admin/AchievementsManagement"));
const ChallengesManagement = lazy(() => import("./pages/admin/ChallengesManagement"));
const GamificationOverview = lazy(() => import("./pages/admin/GamificationOverview"));
const CategoriesManagement = lazy(() => import("./pages/admin/CategoriesManagement"));
const FeatureFlags = lazy(() => import("./pages/admin/FeatureFlags"));
const InvitationsAdmin = lazy(() => import("./pages/admin/Invitations"));
const MarketingCampaigns = lazy(() => import("./pages/admin/MarketingCampaigns"));
const BusinessGrowthDashboard = lazy(() => import("./pages/admin/BusinessGrowthDashboard"));
const AiGovernance = lazy(() => import("./pages/admin/AiGovernance"));
const RuntimeCheck = lazy(() => import("./pages/admin/RuntimeCheck"));
const PrivacyAdmin = lazy(() => import("./pages/admin/PrivacyAdmin"));
const AccountLifecycleAdmin = lazy(() => import("./pages/admin/AccountLifecycle"));
const ComplianceReport = lazy(() => import("./pages/admin/ComplianceReport"));
const BootcampsManagement = lazy(() => import("./pages/admin/BootcampsManagement"));
const BootcampEditor = lazy(() => import("./pages/admin/BootcampEditor"));
const BootcampClassesManagement = lazy(() => import("./pages/admin/BootcampClassesManagement"));
const Bootcamps = lazy(() => import("./pages/Bootcamps"));
const BootcampDetail = lazy(() => import("./pages/BootcampDetail"));
const BootcampClassLanding = lazy(() => import("./pages/BootcampClassLanding"));
const BootcampApplication = lazy(() => import("./pages/BootcampApplication"));
const BootcampApplicationsAdmin = lazy(() => import("./pages/admin/BootcampApplicationsAdmin"));
const BootcampClassWorkspace = lazy(() => import("./pages/admin/BootcampClassWorkspace"));
const MyBootcamps = lazy(() => import("./pages/MyBootcamps"));
const BootcampClassroom = lazy(() => import("./pages/BootcampClassroom"));
const BootcampOnboarding = lazy(() => import("./pages/BootcampOnboarding"));
const BootcampCertificate = lazy(() => import("./pages/BootcampCertificate"));
const BootcampPromotionsManagement = lazy(() => import("./pages/admin/BootcampPromotionsManagement"));
const BootcampAnalytics = lazy(() => import("./pages/admin/BootcampAnalytics"));
const BootcampOperationsHub = lazy(() => import("./pages/admin/BootcampOperationsHub"));
const BootcampRefundsAdmin = lazy(() => import("./pages/admin/BootcampRefundsAdmin"));
const BootcampBroadcastsGlobal = lazy(() => import("./pages/admin/BootcampBroadcastsGlobal"));
const BootcampAuditLogAdmin = lazy(() => import("./pages/admin/BootcampAuditLogAdmin"));

// Lazy load user routes
const Invitations = lazy(() => import("./pages/Invitations"));

// Lazy load public marketing pages
const PartnerCampaign = lazy(() => import("./pages/PartnerCampaign"));

// Lazy load instructor routes
const InstructorDashboard = lazy(() => import("./pages/instructor/InstructorDashboard"));
const InstructorBootcamps = lazy(() => import("./pages/instructor/InstructorBootcamps"));
const FeedbackView = lazy(() => import("./pages/instructor/FeedbackView"));
const AnnouncementsPage = lazy(() => import("./pages/instructor/AnnouncementsPage"));
const CertificatesManagement = lazy(() => import("./pages/instructor/CertificatesManagement"));

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 2 * 60 * 1000, // 2 minutes default
        gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
        refetchOnWindowFocus: false, // Don't refetch on window focus by default
        retry: 1, // Only retry failed requests once
      },
    },
  });

const App = () => {
  useScrollbarVisibility();
  
  return (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange storageKey="aicavite-theme">
    <TooltipProvider>
      <ThemeMetaSync />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LanguageInitializer>
        <ConsentProvider>
        <NativeShellGate />
        <PushNotificationsBootstrap />
        <ScrollToTop />
        <SiteWidePromotionBanner />
        <PromotionPopup />
        <CookieBanner />
        <PrivacyTrigger />
        <Suspense fallback={<RouteFallback />}>
          {/*
            MaintenanceGuard is mounted ONCE around the entire route tree.
            It uses useLocation() internally, so it reacts to navigation
            without re-mounting on every page change. This avoids the 200ms+
            spinner flash that used to appear on every navigation when each
            route was wrapped individually.
          */}
          <MaintenanceGuard>
          <Routes>
          {/* Public routes (auth + maintenance page itself) */}
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />

          {/* Public marketing routes (no dashboard chrome) */}
          <Route path="/" element={<Index />} />
          <Route path="/partner/:slug" element={<PartnerCampaign />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:slug" element={<CourseDetail />} />
          <Route path="/courses/id/:id" element={<CourseDetail />} />
          <Route path="/bundles" element={<Bundles />} />
          <Route path="/bundles/:slug" element={<BundleDetail />} />
          <Route path="/bootcamps" element={<Bootcamps />} />
          <Route path="/bootcamps/:slug" element={<BootcampDetail />} />
          <Route path="/bootcamps/:slug/turmas/:classCode" element={<BootcampClassLanding />} />
          <Route path="/bootcamps/:slug/apply" element={<AuthRoute><BootcampApplication /></AuthRoute>} />
          <Route path="/checkout/pix/:courseId" element={<AuthRoute><CheckoutPix /></AuthRoute>} />
          <Route path="/checkout/pix-cart" element={<AuthRoute><CheckoutPixCart /></AuthRoute>} />
          <Route path="/learn/:courseSlug" element={<AuthRoute><CourseViewer /></AuthRoute>} />
          <Route path="/learn/id/:courseId" element={<AuthRoute><CourseViewer /></AuthRoute>} />
          {/* Public certificate verification page (shareable, no auth required) */}
          <Route path="/certificate/:certificateId" element={<Certificate />} />
          {/* Backwards-compat redirect from old plural route */}
          <Route path="/certificates/:certificateId" element={<CertificateRedirect />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
          <Route path="/bootcamp-certificate/:code" element={<BootcampCertificate />} />
          {/* Prep Premium public landing (gated inside) */}
          <Route path="/prep" element={<PrepLanding />} />

          {/* ==========================================================
              Persistent dashboard shell — sidebar + topbar mount ONCE.
              Only the <Outlet /> swaps when navigating between these
              routes, so no full-page reload flicker.
              ========================================================== */}
          <Route element={<AppShellLayout />}>
            {/* In-shell pages that don't strictly require auth (existing behavior) */}
            <Route path="/faq" element={<FAQ />} />
            <Route path="/support" element={<Support />} />

            {/* Authenticated user routes */}
            <Route path="/dashboard" element={<AuthRoute><Dashboard /></AuthRoute>} />
            <Route path="/my-courses" element={<AuthRoute><MyCourses /></AuthRoute>} />
            <Route path="/wishlist" element={<AuthRoute><Wishlist /></AuthRoute>} />
            <Route path="/announcements" element={<AuthRoute><Announcements /></AuthRoute>} />
            <Route path="/gamification" element={<AuthRoute><Gamification /></AuthRoute>} />
            <Route path="/my-analytics" element={<AuthRoute><MyAnalytics /></AuthRoute>} />
            <Route path="/my-performance" element={<AuthRoute><MyPerformance /></AuthRoute>} />
            <Route path="/profile" element={<AuthRoute><Profile /></AuthRoute>} />
            <Route path="/profile/edit" element={<AuthRoute><EditProfile /></AuthRoute>} />
            <Route path="/settings" element={<AuthRoute><Settings /></AuthRoute>} />
            <Route path="/account/privacy" element={<AuthRoute><AccountPrivacy /></AuthRoute>} />
            <Route path="/account/danger-zone" element={<AuthRoute><AccountDangerZone /></AuthRoute>} />
            <Route path="/messages" element={<AuthRoute><Messages /></AuthRoute>} />
            <Route path="/payment-history" element={<AuthRoute><PaymentHistory /></AuthRoute>} />
            <Route path="/subscription-management" element={<AuthRoute><UserSubscriptionManagement /></AuthRoute>} />
            <Route path="/invitations" element={<AuthRoute><Invitations /></AuthRoute>} />

            {/* Prep Premium — student */}
            <Route path="/prep/dashboard" element={<AuthRoute><PrepDashboard /></AuthRoute>} />
            <Route path="/prep/contests" element={<AuthRoute><PrepContests /></AuthRoute>} />
            <Route path="/prep/contests/:slug" element={<AuthRoute><PrepContestDetail /></AuthRoute>} />
            <Route path="/prep/simulations/:id" element={<AuthRoute><PrepSimulationRoom /></AuthRoute>} />
            <Route path="/prep/simulations/:id/result/:attemptId" element={<AuthRoute><PrepSimulationResult /></AuthRoute>} />
            <Route path="/prep/interviews" element={<AuthRoute><PrepInterviews /></AuthRoute>} />
            <Route path="/prep/interviews/mocks/new" element={<AuthRoute><PrepMockNew /></AuthRoute>} />
            <Route path="/prep/interviews/mocks/:id" element={<AuthRoute><PrepMockRoom /></AuthRoute>} />
            <Route path="/prep/interviews/mocks/:id/feedback" element={<AuthRoute><PrepMockFeedback /></AuthRoute>} />
            <Route path="/prep/interviews/:slug" element={<AuthRoute><PrepInterviewTrackDetail /></AuthRoute>} />
            <Route path="/prep/study-plan" element={<AuthRoute><PrepStudyPlan /></AuthRoute>} />

            {/* Prep Premium — admin */}
            <Route path="/admin/prep" element={<AdminRoute><PrepAdminHub /></AdminRoute>} />
            <Route path="/admin/prep/settings" element={<AdminRoute><PrepSettingsPage /></AdminRoute>} />
            <Route path="/admin/prep/products" element={<AdminRoute><PrepProductsPage /></AdminRoute>} />
            <Route path="/admin/prep/questions" element={<AdminRoute><PrepQuestionsPage /></AdminRoute>} />
            <Route path="/admin/prep/rubrics" element={<AdminRoute><PrepRubricsPage /></AdminRoute>} />
            <Route path="/admin/prep/contests" element={<AdminRoute><PrepAdminContestsPage /></AdminRoute>} />
            <Route path="/admin/prep/interview-tracks" element={<AdminRoute><PrepAdminInterviewTracks /></AdminRoute>} />
            <Route path="/admin/prep/access" element={<AdminRoute><PrepAccessPage /></AdminRoute>} />

            {/* Instructor / advanced routes */}
            <Route path="/learning-analytics" element={<InstructorRoute><LearningAnalytics /></InstructorRoute>} />
            <Route path="/settings/ai" element={<InstructorRoute><AiSettings /></InstructorRoute>} />
            <Route path="/instructor/dashboard" element={<InstructorRoute><InstructorDashboard /></InstructorRoute>} />
            <Route path="/instructor/bootcamps" element={<InstructorRoute><InstructorBootcamps /></InstructorRoute>} />
            <Route path="/instructor/feedback" element={<InstructorRoute><FeedbackView /></InstructorRoute>} />
            <Route path="/instructor/announcements" element={<InstructorRoute><AnnouncementsPage /></InstructorRoute>} />
            <Route path="/instructor/certificates" element={<InstructorRoute><CertificatesManagement /></InstructorRoute>} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/control-center" element={<AdminRoute><ControlCenter /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><UsersManagement /></AdminRoute>} />
            <Route path="/admin/courses" element={<AdminRoute><CoursesManagement /></AdminRoute>} />
            <Route path="/admin/courses/new" element={<AdminRoute><CourseEditor /></AdminRoute>} />
            <Route path="/admin/courses/:courseId/edit" element={<AdminRoute><CourseEditor /></AdminRoute>} />
            <Route path="/admin/bootcamps" element={<AdminRoute><BootcampsManagement /></AdminRoute>} />
            <Route path="/admin/bootcamps/new" element={<AdminRoute><BootcampEditor /></AdminRoute>} />
            <Route path="/admin/bootcamps/:bootcampId/edit" element={<AdminRoute><BootcampEditor /></AdminRoute>} />
            <Route path="/admin/bootcamps/:bootcampId/classes" element={<AdminRoute><BootcampClassesManagement /></AdminRoute>} />
            <Route path="/admin/bootcamps/classes/:classId/applications" element={<AdminRoute><BootcampApplicationsAdmin /></AdminRoute>} />
            <Route path="/admin/bootcamps/classes/:classId/workspace" element={<InstructorRoute><BootcampClassWorkspace /></InstructorRoute>} />
            <Route path="/admin/bootcamps/promotions" element={<AdminRoute><BootcampPromotionsManagement /></AdminRoute>} />
            <Route path="/admin/bootcamps/analytics" element={<AdminRoute><BootcampAnalytics /></AdminRoute>} />
            <Route path="/admin/bootcamps/operations" element={<AdminRoute><BootcampOperationsHub /></AdminRoute>} />
            <Route path="/admin/bootcamps/refunds" element={<AdminRoute><BootcampRefundsAdmin /></AdminRoute>} />
            <Route path="/admin/bootcamps/broadcasts" element={<AdminRoute><BootcampBroadcastsGlobal /></AdminRoute>} />
            <Route path="/admin/bootcamps/audit" element={<AdminRoute><BootcampAuditLogAdmin /></AdminRoute>} />
            <Route path="/my-bootcamps" element={<AuthRoute><MyBootcamps /></AuthRoute>} />
            <Route path="/my-bootcamps/:classId" element={<AuthRoute><BootcampClassroom /></AuthRoute>} />
            <Route path="/my-bootcamps/:classId/onboarding" element={<AuthRoute><BootcampOnboarding /></AuthRoute>} />
            <Route path="/admin/categories" element={<AdminRoute><CategoriesManagement /></AdminRoute>} />
            <Route path="/admin/enrollments" element={<AdminRoute><StudentEnrollments /></AdminRoute>} />
            <Route path="/admin/activity-tracking" element={<AdminRoute><ActivityTracking /></AdminRoute>} />
            <Route path="/admin/analytics" element={<AdminRoute><Analytics /></AdminRoute>} />
            <Route path="/admin/advanced-analytics" element={<AdminRoute><AdvancedAnalytics /></AdminRoute>} />
            <Route path="/admin/business-growth" element={<AdminRoute><BusinessGrowthDashboard /></AdminRoute>} />
            <Route path="/admin/quiz-analytics" element={<AdminRoute><QuizAnalyticsDashboard /></AdminRoute>} />
            <Route path="/admin/ai-quiz-tools" element={<AdminRoute><AiQuizTools /></AdminRoute>} />
            <Route path="/admin/ai-governance" element={<AdminRoute><AiGovernance /></AdminRoute>} />
            <Route path="/admin/instructors" element={<AdminRoute><InstructorsManagement /></AdminRoute>} />
            <Route path="/admin/feedback" element={<AdminRoute><FeedbackManagement /></AdminRoute>} />
            <Route path="/admin/promotions" element={<AdminRoute><PromotionsManagement /></AdminRoute>} />
            <Route path="/admin/discounts" element={<AdminRoute><DiscountsManagement /></AdminRoute>} />
            <Route path="/admin/subscription-management" element={<AdminRoute><SubscriptionManagement /></AdminRoute>} />
            <Route path="/admin/platform-plans" element={<AdminRoute><PlatformPlans /></AdminRoute>} />
            <Route path="/admin/payment-operations" element={<AdminRoute><PaymentOperations /></AdminRoute>} />
            <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
            <Route path="/admin/abandoned-carts" element={<AdminRoute><AdminAbandonedCarts /></AdminRoute>} />
            <Route path="/admin/recommendations" element={<AdminRoute><AdminRecommendations /></AdminRoute>} />
            <Route path="/admin/bundles" element={<AdminRoute><BundlesManagement /></AdminRoute>} />
            <Route path="/admin/payment-health" element={<AdminRoute><PaymentHealthDashboard /></AdminRoute>} />
            <Route path="/admin/access-management" element={<AdminRoute><AccessManagement /></AdminRoute>} />
            <Route path="/admin/customer-support" element={<AdminRoute><CustomerSupport /></AdminRoute>} />
            <Route path="/admin/role-management" element={<AdminRoute><RoleManagement /></AdminRoute>} />
            <Route path="/admin/communications" element={<AdminRoute><CommunicationsManagement /></AdminRoute>} />
            <Route path="/admin/newsletter" element={<AdminRoute><NewsletterManagement /></AdminRoute>} />
            <Route path="/admin/newsletter/analytics" element={<AdminRoute><NewsletterAnalytics /></AdminRoute>} />
            <Route path="/admin/emails" element={<AdminRoute><EmailMonitor /></AdminRoute>} />
            <Route path="/admin/blog" element={<AdminRoute><BlogManagement /></AdminRoute>} />
            <Route path="/admin/security" element={<AdminRoute><SecurityDashboard /></AdminRoute>} />
            <Route path="/admin/security-audit" element={<AdminRoute><SecurityAudit /></AdminRoute>} />
            <Route path="/admin/integration-testing" element={<AdminRoute><IntegrationTesting /></AdminRoute>} />
            <Route path="/admin/security-vulnerability-testing" element={<AdminRoute><SecurityVulnerabilityTesting /></AdminRoute>} />
            <Route path="/admin/security-scanner" element={<AdminRoute><SecurityScanner /></AdminRoute>} />
            <Route path="/admin/security-compliance" element={<AdminRoute><SecurityCompliance /></AdminRoute>} />
            <Route path="/admin/user-intelligence" element={<AdminRoute><UserIntelligence /></AdminRoute>} />
            <Route path="/admin/ab-testing" element={<AdminRoute><ABTestingDashboard /></AdminRoute>} />
            <Route path="/admin/certificates" element={<AdminRoute><AdminCertificatesManagement /></AdminRoute>} />
            <Route path="/admin/silva-coins" element={<AdminRoute><SilvaCoinsManagement /></AdminRoute>} />
            <Route path="/admin/gamification" element={<AdminRoute><GamificationOverview /></AdminRoute>} />
            <Route path="/admin/badges" element={<AdminRoute><BadgesManagement /></AdminRoute>} />
            <Route path="/admin/achievements" element={<AdminRoute><AchievementsManagement /></AdminRoute>} />
            <Route path="/admin/challenges" element={<AdminRoute><ChallengesManagement /></AdminRoute>} />
            <Route path="/admin/feature-flags" element={<AdminRoute><FeatureFlags /></AdminRoute>} />
            <Route path="/admin/invitations" element={<AdminRoute><InvitationsAdmin /></AdminRoute>} />
            <Route path="/admin/marketing-campaigns" element={<AdminRoute><MarketingCampaigns /></AdminRoute>} />
            <Route path="/admin/runtime-check" element={<AdminRoute><RuntimeCheck /></AdminRoute>} />
            <Route path="/admin/privacy" element={<AdminRoute><PrivacyAdmin /></AdminRoute>} />
            <Route path="/admin/privacy/report" element={<AdminRoute><ComplianceReport /></AdminRoute>} />
            <Route path="/admin/account-lifecycle" element={<AdminRoute><AccountLifecycleAdmin /></AdminRoute>} />
          </Route>

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </MaintenanceGuard>
        </Suspense>
        </ConsentProvider>
        </LanguageInitializer>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  );
};

export default App;
